import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Shield, CheckCircle, XCircle, Loader2 } from 'lucide-react';

export default function DeclineAppointment() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // 'processing' | 'success' | 'error'
  const [message, setMessage] = useState('Processing your decline request...');
  const [appointmentDetails, setAppointmentDetails] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [showReasonInput, setShowReasonInput] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      try {
        const token = searchParams?.get('token');
        
        if (!token) {
          setStatus('error');
          setMessage('Invalid decline link. Token is missing.');
          return;
        }

        // Find appointment by approval token
        const { data: appointment, error: fetchError } = await supabase?.from('appointments')?.select('*')?.eq('approval_token', token)?.single();

        if (fetchError || !appointment) {
          setStatus('error');
          setMessage('Appointment not found or link has expired.');
          return;
        }

        // Check if already processed
        if (appointment?.status !== 'pending') {
          setStatus('error');
          setMessage(`This appointment has already been ${appointment?.status}.`);
          setAppointmentDetails(appointment);
          return;
        }

        // Show reason input form
        setAppointmentDetails(appointment);
        setShowReasonInput(true);
        setStatus('ready');
        setMessage('Please provide a reason for declining this appointment.');

      } catch (err) {
        console.error('Validation error:', err);
        setStatus('error');
        setMessage('An unexpected error occurred. Please contact support.');
      }
    };

    validateToken();
  }, [searchParams]);

  const handleDecline = async () => {
    if (!declineReason?.trim()) {
      alert('Please provide a reason for declining.');
      return;
    }

    setStatus('processing');
    setMessage('Declining appointment...');
    setShowReasonInput(false);

    try {
      const token = searchParams?.get('token');

      // Update appointment status to declined with reason
      const { error: updateError } = await supabase?.from('appointments')?.update({ 
          status: 'declined',
          decline_reason: declineReason,
          updated_at: new Date()?.toISOString()
        })?.eq('approval_token', token);

      if (updateError) {
        console.error('Error declining appointment:', updateError);
        setStatus('error');
        setMessage('Failed to decline appointment. Please try again or contact support.');
        setShowReasonInput(true);
        return;
      }

      // Success
      setStatus('success');
      setMessage('Appointment declined successfully.');

      // Redirect to dashboard after 3 seconds
      setTimeout(() => {
        navigate('/appointment-management-dashboard');
      }, 3000);

    } catch (err) {
      console.error('Decline processing error:', err);
      setStatus('error');
      setMessage('An unexpected error occurred. Please contact support.');
      setShowReasonInput(true);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-red-600 to-red-700 p-8 text-center">
            <Shield className="w-16 h-16 text-white mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-white">
              Decline Appointment
            </h1>
          </div>

          {/* Content */}
          <div className="p-8">
            {status === 'processing' && (
              <div className="text-center py-12">
                <Loader2 className="w-16 h-16 text-red-600 animate-spin mx-auto mb-4" />
                <p className="text-xl text-gray-700">{message}</p>
              </div>
            )}

            {status === 'ready' && showReasonInput && appointmentDetails && (
              <div className="py-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Appointment Details:</h3>
                  <div className="space-y-2 text-gray-700">
                    <p><strong>Client:</strong> {appointmentDetails?.client_name}</p>
                    <p><strong>Date:</strong> {new Date(appointmentDetails.appointment_date)?.toLocaleString()}</p>
                    <p><strong>Purpose:</strong> {appointmentDetails?.purpose}</p>
                    {appointmentDetails?.purpose_details && (
                      <p><strong>Details:</strong> {appointmentDetails?.purpose_details}</p>
                    )}
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-gray-900 font-semibold mb-2">
                    Reason for Declining <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={declineReason}
                    onChange={(e) => setDeclineReason(e?.target?.value)}
                    placeholder="Please explain why you cannot attend this appointment..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                    rows={4}
                    required
                  />
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={handleDecline}
                    className="flex-1 bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors"
                  >
                    Confirm Decline
                  </button>
                  <button
                    onClick={() => navigate('/appointment-management-dashboard')}
                    className="flex-1 bg-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {status === 'success' && (
              <div className="text-center py-12">
                <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {message}
                </h2>
                {appointmentDetails && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6 text-left">
                    <h3 className="font-semibold text-gray-900 mb-3">Declined Appointment:</h3>
                    <div className="space-y-2 text-gray-700">
                      <p><strong>Client:</strong> {appointmentDetails?.client_name}</p>
                      <p><strong>Date:</strong> {new Date(appointmentDetails.appointment_date)?.toLocaleString()}</p>
                      <p><strong>Reason:</strong> {declineReason}</p>
                    </div>
                  </div>
                )}
                <p className="text-gray-600">
                  The client has been notified. Redirecting to dashboard...
                </p>
              </div>
            )}

            {status === 'error' && (
              <div className="text-center py-12">
                <XCircle className="w-20 h-20 text-red-500 mx-auto mb-6" />
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Unable to Process Request
                </h2>
                <p className="text-lg text-gray-700 mb-6">
                  {message}
                </p>
                {appointmentDetails && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6 text-left">
                    <h3 className="font-semibold text-gray-900 mb-3">Appointment Details:</h3>
                    <div className="space-y-2 text-gray-700">
                      <p><strong>Client:</strong> {appointmentDetails?.client_name}</p>
                      <p><strong>Date:</strong> {new Date(appointmentDetails.appointment_date)?.toLocaleString()}</p>
                      <p><strong>Status:</strong> <span className="capitalize">{appointmentDetails?.status}</span></p>
                    </div>
                  </div>
                )}
                <button
                  onClick={() => navigate('/appointment-management-dashboard')}
                  className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  Go to Dashboard
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-gray-600 text-sm">
            Sailors Bail Bonds - Professional Bail Services
          </p>
        </div>
      </div>
    </div>
  );
}